/**
 * B2B トレーナー線（coaches.kalori.jp + シート課金）の **公開スイッチ**。
 *
 * ここに定数が 1 つだけある理由: 公開に必要な面が 3 つの worker と iOS に散っており、
 * 面ごとに条件を書くと「LP は売っているのにアプリに入口が無い」「入口はあるのに
 * Checkout が無い」のような**半分だけ公開された状態**が必ず起きる。実際 2026-08-10 の
 * 時点で、トレーナー画面は会員様に「マイページ → 招待コードを入力」と案内していたのに、
 * その行は iOS の `#if DEBUG` の中にしか無かった。
 *
 * ⚠️ **この定数を読む側に条件を足さない。** 「ここは公開でもいい / ここはまだ」を
 * 面ごとに判断し始めた瞬間にスイッチではなくなる。段階公開が要るなら、その段を
 * **この定数の隣に**名前付きで足す（呼び出し側の `&&` にしない）。
 *
 * ## このスイッチが動かす面（全部・追加したらここに書く）
 *
 * | 面 | off（現在） | on |
 * |---|---|---|
 * | web `/business/coaches/`（ja/en） | 「利用を申請する」→ {@link TRAINER_APPLY_URL}（審査制・`docs/architecture/b2b-onboarding.md`） | 「はじめる」→ {@link TRAINER_SIGNUP_URL}（審査なし） |
 * | coaches.kalori.jp `/login`・登録なし画面 | 「はじめての方」→ `/apply`（審査制） | 「はじめての方」→ 申し込み導線 |
 *
 * **会員側の入口**（app-api `/subscriptions/status` の `orgSeatEntryEnabled` ＝ iOS マイページの
 * 「トレーナーの招待コードを入力」）は、全体公開の**手前の段**として
 * {@link TRAINER_SEAT_ENTRY_ENABLED} が決める（全体公開すれば自動で on になる）。
 *
 * ⚠️ **web は SSG なので、この定数は「ビルド時」に効く**（デプロイで反映＝夜間ビルドか push）。
 * app-api / trainer はランタイムだが、どのみち同じ commit から出るので実質同時に切り替わる。
 * 「1 行変えて push」がスイッチの操作方法で、それ以外の操作（KV・環境変数・管理画面）を作らない
 * — 設定面を増やすと、コードと本番の状態が食い違う経路が増える。
 *
 * ## on にする前に満たしていること（未達で on にすると preflight テストが赤くなる）
 *
 * 単一の正 = `cloudflare/workers/app-api/src/utils/__tests__/trainer-launch-preflight.test.ts`。
 * ここに散文で書くと必ず腐るので、**条件はテストが持つ**（このコメントは索引）。
 *
 * 1. Stripe livemode が開いていて、billing worker がデプロイ済み（route 有効・secret 登録済み）
 * 2. 申し込み導線（{@link TRAINER_SIGNUP_URL}）が実在する
 * 3. お試しを終了して開始する導線が繋がっている（trainer 画面が案内している操作の実体）
 * 4. 毎時リコンサイル（連携人数 → Stripe quantity）が動いている
 * 5. **trainer.kalori.jp が CF Access の裏に居ない**（2026-08-14 追加）。Access は「到達可能か」、
 *    このスイッチは「導線を出すか」で**別の軸**なので、両方 on だと *公開されている LP の CTA が
 *    ログイン壁に着地する* ＝ 一般客だけが行き止まりに当たる。**Access を外すのが公開作業の一部**。
 *
 * ⚠️ 2026-08-14 に実測したところ、**repo 側で未達なのは 5 だけ**（1〜4 は満たしている）。
 *    → 2026-09-06 に Access を外した（利用申請制の招待先が Access を通れない問題の解消・
 *    `docs/architecture/b2b-onboarding.md`）。**5 も満たした**ので、on にする条件は揃っている。
 *    off のままなのは審査制（申請 → 承認 → 招待）で始めるという判断であって、未達があるからではない。
 *
 * ⚠️ **Stripe livemode は既に有効**（同日 API で実測 = `stripe get /v1/balance --live` が
 *    `livemode: true`・`charges_enabled` / `payouts_enabled` / `details_submitted` すべて true・
 *    `requirements` 空）。#700 が長らく「未申請」と書いていたが、それは**更新漏れ**だった。
 *    残るのは審査ではなく **live 側に product / price / webhook をまだ作っていないこと**
 *    （Terraform の state は sandbox のもの。手順 = `infra/stripe/README.md` §livemode に上げるとき）。
 *    ⚠️ **外部サービスの状態を Issue の記述で判断しない** — 1 コマンドで実測できる。
 *
 * 経緯・落とし穴 = `docs/archive/memory-2026-08-30/project_b2b_trainer_seat_dashboard.md` / Issue #700 #705 #706 #707。
 */
/**
 * トレーナー線をセルフサーブで一般公開するか。
 *
 * **これがこのファイルの存在理由**。切り替えはこの 1 行の編集 + push だけ。
 */
export const TRAINER_SELF_SERVE_ENABLED = false;
/**
 * **段階公開の 1 段目**: 会員側の入口（iOS マイページ「トレーナーの招待コードを入力」）だけを出す。
 *
 * 全体公開 = 「ジムが自分で申し込んで課金まで進める」状態だが、その手前に **こちらが手で org を
 * 作って席を渡す段**がある（最初の 1〜3 社と社内検証。admin `/trainers` で org を作り招待する運用）。
 * このとき会員様に必要なのは招待コードの入力欄だけで、LP の CTA も `/signup` も要らない
 * — なのに全体公開スイッチしか無いと、**渡した席を受け取る手段がアプリに存在しない**
 * （トレーナー画面は「マイページ → 招待コードを入力」と案内するので、案内先が無い状態になる）。
 *
 * ⚠️ **読む側で `||` を書かない。** 段の合成はこのファイルに閉じ込める（呼び出し側で
 * 「ここは公開でもいい」を判断し始めた瞬間にスイッチではなくなる — 冒頭の規約そのもの）。
 * ⚠️ iOS は**サーバのこの値を読む実装で既に出荷済み**なので、ここを変えて app-api をデプロイ
 * すればアプリ更新も審査も無しで行が出る／消える。
 * ⚠️ この段は**入口だけ**。席の発行（coaches.kalori.jp `/seats`）と claim の実体は
 * スイッチと無関係に常時動いているので、on にしても新しく通る経路は増えない。
 */
const SEAT_ENTRY_STAGE = true;
/**
 * 会員側に招待コードの入力口を見せるか（app-api `/subscriptions/status` の `orgSeatEntryEnabled`）。
 *
 * **これを読む側は素でそのまま使う**（`&&` も `||` も足さない）。全体公開したときに
 * 段の指定を消し忘れても on のままになるよう、{@link TRAINER_SELF_SERVE_ENABLED} を含めてある。
 */
export const TRAINER_SEAT_ENTRY_ENABLED = TRAINER_SELF_SERVE_ENABLED || SEAT_ENTRY_STAGE;
/**
 * 食事指導者向け管理画面（Kalori for Coaches）の**対外ホスト**。
 *
 * 2026-09-06 に `trainer.kalori.jp` → `coaches.kalori.jp` へ付け替えた。対外名を "for Coaches"
 * （トレーナーに限らず管理栄養士なども対象）にしたのに、招待メール・アドレスバーに毎回 `trainer` が
 * 出ていたため。**内部の識別子（worker 名 `kalori-trainer`・`TRAINER_*`・`app_org_*`・台帳の
 * `line = 'trainer'`）は変えない** — 変えると移行そのものが事故の源になる（§8 の原則）。
 *
 * 旧ホストは worker 側で 301（{@link TRAINER_LEGACY_ORIGIN}）。送付済みの招待メール・出荷済み iOS の
 * リンク・Stripe に登録済みの戻り先が生きるようにするためで、撤去の予定は置かない（維持費ゼロ）。
 *
 * トレーナー向けの面はこのホストに集約する（web は情報サイト、trainer は業務画面、という役割分担を崩さない）。
 */
export const TRAINER_ORIGIN = "https://coaches.kalori.jp";
/** 旧ホスト。ここへ来た要求は同じパスで {@link TRAINER_ORIGIN} へ 301（trainer worker の先頭）。 */
export const TRAINER_LEGACY_ORIGIN = "https://trainer.kalori.jp";
/**
 * 申し込みの入口 URL（LP の CTA と、iOS/メールの案内から指す先）。
 * `/signup` はログイン済み（招待受諾済み）のオーナーが同意して Stripe Checkout へ進む面で、
 * org の作成は運営側（docs/architecture/b2b-onboarding.md §8）。
 */
export const TRAINER_SIGNUP_URL = `${TRAINER_ORIGIN}/signup`;
/** トレーナー向けダッシュボードの入口（既に契約済みの事業者が使う）。 */
export const TRAINER_DASHBOARD_URL = `${TRAINER_ORIGIN}/`;
/**
 * 審査制（現在）の利用申請フォーム。LP の CTA はここへ送る（2026-09-06〜・フォーム本体は
 * コンソール側 = `./apply.ts`）。developer 線の対は {@link DEVELOPER_APPLY_URL}。
 */
export const TRAINER_APPLY_URL = `${TRAINER_ORIGIN}/apply`;
export const DEVELOPER_APPLY_URL = "https://console.kalori.jp/apply";
/**
 * kalori.jp 側の対外ページ。パスも `coaches` に揃えた（旧 `/business/coaches/`・`/terms/coaches/` は
 * web の `_redirects` で 301）。管理画面・admin の案内メール・Stripe の product url が指す先。
 */
export const TRAINER_LP_URL = "https://kalori.jp/ja/business/coaches/";
export const TRAINER_TERMS_URL = "https://kalori.jp/ja/terms/coaches/";
// =============================================================
// 料金・お試しの契約値（worker をまたいで同じ数字を読む必要があるものだけ）
// =============================================================
/**
 * 無料お試しの日数。**Stripe の `trial_period_days` に渡る値がこれ**。
 *
 * ⚠️ 対外の文言（LP `business/coaches` / 規約第5条 / 特商法 / llms.txt / FAQ の ja・en）は
 * 別に「14日」と書いてある。数字を変えるときはこの定数と文言を同時に動かすこと
 * — ここだけ変えると「サイトは14日と書いてあるのに実際は違う」になる。
 */
export const TRAINER_TRIAL_PERIOD_DAYS = 14;
/**
 * **お試し期間中**に連携できる会員の上限。
 *
 * ⚠️ これは原価の話ではなく **消費者 Premium の裏口を塞ぐため**。シートは会員に Premium を
 * 与えるので、無制限のお試しは「トレーナーを名乗って友人 N 人に 14 日ぶんの Premium を配る」
 * 経路になる（消費者側のお試しは AI 使用日 7 日分なので、B2B の方が条件が良いという逆転まで起きる）。
 * 1 名に絞ると取り分が ¥480 相当に潰れ、申請・承認・カード登録を経る手間に見合わなくなる。
 *
 * 有料のシートには同じ穴が無い（¥1,960〜1,600 > 消費者プラン ¥960 なので裁定が成立しない）。
 *
 * 1 名で足りると判断したのは「ダッシュボードの価値は顧客 1 人でも完全に伝わる。2 人目以降は
 * 同じ体験の反復」だから。**緩める（1→3→無制限）のは顧客有利なので通知不要** — 厳しい側から
 * 始めるのが正しい順序。
 *
 * ⚠️ **この上限を「待たせる壁」にしないこと。** 2 人目を追加しようとした事業者は
 * 「気に入ったので本格的に始めたい人」＝ 最も転換に近い相手で、そこで 11 日待たせるのは最悪。
 * 上限に当たったら **その場でお試しを終了して開始できる導線**を返す（`trialSkippable`）。
 *
 * **読み手は 2 つ**: app-api（発行ゲート `canCreateSeat`）と billing（リコンサイル時の
 * 「お試し中なのに上限超え」検知）。別々に literal を持つとズレるのでここが正。
 */
export const TRAINER_TRIAL_MAX_SEATS = 1;
/**
 * シート price の `lookup_key`（`infra/stripe/trainer.tf` の宣言と対）。
 *
 * price ID を env/secret に持たせないための間接参照。Terraform で price を作り直すと
 * ID は変わるが lookup_key は引き継げるので、コード側は無改修で追随できる。
 */
export const TRAINER_SEAT_PRICE_LOOKUP_KEY = "trainer_seat_monthly_jpy";
/**
 * 課金対象 item を見分ける目印（price の `metadata.seat`）。**`infra/stripe/trainer.tf` と対**で、
 * 片方だけ変えると seat_limit の更新が黙って止まる。
 *
 * lookup_key ではなく metadata を判別子にしているのは、lookup_key がアカウント内で一意という
 * 制約を持ち、価格改定で price を作り直すたびに衝突を解く必要があるため。metadata に制約は無い。
 */
export const TRAINER_SEAT_ITEM_MARKER = "member";
/** トレーナー契約への明示同意の版。招待受諾とセルフサーブが同じ値を保存する。 */
// 公開中の /terms/coaches/ の最終更新日（web/src/pages/[locale]/terms/coaches/index.astro）。
export const TRAINER_TERMS_VERSION = "2026-08-13";
// =============================================================
// B2B 共通の身元（メール確認コード）。実体は ./auth.ts（設計 = docs/architecture/b2b-onboarding.md §8）
// =============================================================
// =============================================================
// B2B 共通の利用申請（各コンソールの /apply → kalori-api の内部面 `B2BApplyInternal`）。実体は ./apply.ts
// =============================================================
//# sourceMappingURL=coaches.js.map